import type { CatalogProductDto } from "@island/catalog-core";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import { ProductPicker } from "./ProductPicker.js";

/**
 * Writing a reply.
 *
 * Sending is blocked until the agent has joined. The platform would accept the
 * message either way — staff hold `support:conversations:write` regardless — but a
 * message appearing from someone the customer has not been told is present is
 * exactly the confusion silent monitoring is designed to avoid.
 *
 * ## `/pro`
 *
 * Typing `/pro` opens a catalogue search; picking a result attaches it as a card and
 * removes the command from the text. The agent can then write alongside it, or send
 * the card on its own.
 *
 * Shortened from `/product` so it is the same command on the phone, where every
 * character costs. The old spelling still works: agents have it in muscle memory,
 * and a command that silently became ordinary text would be typed into a customer's
 * transcript before anyone noticed.
 *
 * Recognised **anywhere in the box**, not only at the start. Agents write the
 * sentence first and reach for the product at the end of it — "here is the one you
 * were asking about /pro bpc" is the natural order, and requiring an empty box meant
 * deleting a finished sentence to run the command and then typing it again. Only the
 * command and its search term are removed on picking; everything the agent had
 * already written stays exactly where it was.
 *
 * The trailing command wins, the search term runs to the end of that line, and the
 * command must stand as its own word — so `/products`, and a URL with `/pro` in the
 * path, are ordinary text.
 *
 * Attachments are held as ids and resolved by the API, so the card the customer sees
 * is built from the catalogue rather than from anything this component renders.
 */

/**
 * Above this, a message stops being a recommendation and becomes a catalogue dump.
 *
 * Ten, raised from four. Four was the number at which the reasoning above stops
 * being obviously true, not the number at which it becomes false — and agents were
 * hitting it answering "what do you have for recovery", where five or six products
 * is the honest answer. Mirrors the ceiling in the send route, which is the one
 * that is enforced.
 */
const MAX_PRODUCTS = 10;

/** The short form, and the long one it replaced. Both are accepted. */
const COMMANDS = ["/pro", "/product"] as const;

/**
 * The same small set the customer's widget offers.
 *
 * Not a full emoji keyboard — the agent's machine has one, and shipping a
 * searchable picker into a support tool would cost more than the composer it
 * sits in. These are the ones people actually send to a shop, and an agent
 * answering warmly in the same vocabulary the customer used is worth the row
 * of buttons.
 */
const EMOJIS = [
  "😀",
  "😄",
  "😊",
  "😉",
  "😍",
  "🤙",
  "👍",
  "👋",
  "🙏",
  "🙌",
  "💪",
  "🔥",
  "⭐",
  "🎉",
  "✅",
  "❤️",
  "😅",
  "😂",
  "🤔",
  "😎",
  "🌺",
  "🌴",
  "📦",
  "🚚",
  "💳",
  "❓",
];

/**
 * A product command found in the box.
 *
 * `start` and `end` bracket everything the command owns — the word itself and the
 * search term after it — so picking a product can cut exactly that span out and
 * leave the agent's own sentence untouched.
 */
interface ProductCommand {
  /** The text after the command. Empty shows the first products rather than nothing. */
  readonly query: string;
  readonly start: number;
  readonly end: number;
}

/** Whether a character can sit against the command without being part of it. */
function isBoundary(character: string): boolean {
  return character === "" || /\s/.test(character);
}

/**
 * The product command in the box, or null when there is not one.
 *
 * Three rules, each of them there to stop ordinary text becoming a command:
 *
 * - it must **begin a word** — at the start of the box or after whitespace — so the
 *   `/pro` inside `https://shop/pro/bpc-157` is part of a URL and stays one;
 * - it must **end one**, being either the whole word or followed by a space, so
 *   `/products` is the plural an agent typed and not a misspelling of this;
 * - the search term stops at the **end of the line**, so a command on the first line
 *   of a two-line reply does not swallow the second.
 *
 * The last command wins. An agent who runs the command twice in one reply is working
 * on the second one, and the first has already produced its card.
 */
function productCommand(text: string): ProductCommand | null {
  let found: ProductCommand | null = null;

  for (const command of COMMANDS) {
    for (let at = text.indexOf(command); at !== -1; at = text.indexOf(command, at + 1)) {
      if (at > 0 && !isBoundary(text.charAt(at - 1))) continue;

      const after = text.charAt(at + command.length);

      if (after !== "" && after !== " ") continue;
      if (found !== null && at <= found.start) continue;

      const rest = text.slice(at + command.length + (after === " " ? 1 : 0));
      const newline = rest.indexOf("\n");
      const query = newline === -1 ? rest : rest.slice(0, newline);

      found = { query, start: at, end: text.length - rest.length + query.length };
    }
  }

  return found;
}

/**
 * The reply with the command taken out of it.
 *
 * Used both when a product is picked and when the agent sends while the picker is
 * still open: in either case the command is the agent's own tooling and has no
 * business in a customer's transcript, while the sentence around it is the message.
 */
function withoutCommand(text: string, command: ProductCommand): string {
  return `${text.slice(0, command.start)}${text.slice(command.end)}`;
}

/**
 * What an agent may attach.
 *
 * Wider than the customer's, and deliberately so: the server permits documents to
 * staff only, so an agent can send a returns form while an unsolicited PDF from the
 * public is refused at the intent step. This list only shapes the file dialogue —
 * the rule that matters is enforced server-side.
 */
const ACCEPTED_FILES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/gif",
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "application/pdf",
].join(",");

interface UploadIntent {
  readonly attachmentId: string;
  readonly uploadUrl: string | null;
}

export function Composer({
  disabled,
  requiresJoin,
  onSend,
  onTyping,
  onUpload,
  prefill,
  onPrefillConsumed,
}: {
  readonly disabled: boolean;
  readonly requiresJoin: boolean;
  readonly onSend: (
    text: string,
    productIds: readonly string[],
    attachmentIds: readonly string[],
  ) => Promise<void>;
  readonly onTyping: (active: boolean) => void;
  /**
   * Requests an upload slot and pushes the bytes.
   *
   * Passed in rather than reaching for the API client here: this component renders,
   * and the conversation view already owns the client and the error surface.
   */
  readonly onUpload: (file: File) => Promise<UploadIntent | null>;
  /**
   * Text to drop into the box, from "Edit" on a suggested reply.
   *
   * A prop rather than a ref handle, so the suggestion decides *what* and this
   * component keeps deciding *how* — including where the caret lands and whether
   * the product picker should open, neither of which the caller should know about.
   */
  readonly prefill?: string | null;
  /** Called once the text has been taken, so the same draft is not applied twice. */
  readonly onPrefillConsumed?: () => void;
}) {
  /** Files chosen but not yet sent, held as ids the API will resolve. */
  const [attachments, setAttachments] = useState<readonly { id: string; name: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  /** Chosen products, in the order the agent picked them. */
  const [products, setProducts] = useState<readonly CatalogProductDto[]>([]);
  /** Set while the picker is deliberately closed, so it does not spring back open. */
  const [pickerDismissed, setPickerDismissed] = useState(false);
  const typingRef = useRef(false);

  /**
   * Take a suggested reply into the box.
   *
   * Appended rather than replacing, on the off chance the agent had already started
   * typing — losing somebody's half-written sentence to a button they pressed for
   * convenience would be a poor trade. The caret goes to the end so they can carry
   * straight on.
   */
  useEffect(() => {
    if (prefill === undefined || prefill === null || prefill === "") return;

    setText((current) => (current.trim() === "" ? prefill : `${current.trimEnd()} ${prefill}`));
    setPickerDismissed(true);
    onPrefillConsumed?.();

    const box = boxRef.current;

    if (box !== null) {
      box.focus();
      // After the value lands, so the caret is not moved back by React's re-render.
      window.setTimeout(() => {
        box.selectionStart = box.value.length;
        box.selectionEnd = box.value.length;
      }, 0);
    }
  }, [prefill, onPrefillConsumed]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const command = productCommand(text);
  /** The command the picker is searching for, or null when it is not on screen. */
  const searching =
    command !== null && !pickerDismissed && products.length < MAX_PRODUCTS ? command : null;
  const pickerOpen = searching !== null;

  /**
   * Report typing on the first keystroke, then stop after a pause.
   *
   * Not per keystroke: a typing indicator is a hint, and one frame per character
   * would be the noisiest thing on the socket by an order of magnitude.
   */
  function noteTyping(): void {
    if (requiresJoin) return;

    if (!typingRef.current) {
      typingRef.current = true;
      onTyping(true);
    }

    if (idleTimer.current !== null) clearTimeout(idleTimer.current);

    idleTimer.current = setTimeout(() => {
      typingRef.current = false;
      onTyping(false);
    }, 2_000);
  }

  /**
   * The stop signal, on unmount and only on unmount.
   *
   * `onTyping` was the dependency here, which meant a caller passing an inline
   * arrow — the normal thing to write — turned this cleanup into something that
   * ran on every render of the parent, sending `typing(false)` continuously
   * while the agent was mid-word. The callback is held in a ref so the effect
   * can depend on nothing and still call the current one.
   */
  const onTypingRef = useRef(onTyping);

  useEffect(() => {
    onTypingRef.current = onTyping;
  }, [onTyping]);

  useEffect(
    () => () => {
      if (idleTimer.current !== null) clearTimeout(idleTimer.current);
      // Tell the other side the indicator has stopped, or it hangs there until it
      // times out server-side.
      if (typingRef.current) onTypingRef.current(false);
    },
    [],
  );

  /**
   * Put an emoji where the caret is.
   *
   * Read from the textarea rather than tracked separately: focus never leaves
   * it, because the button's `mousedown` is prevented above.
   */
  function insertEmoji(emoji: string): void {
    const box = boxRef.current;

    if (box === null) {
      setText((current) => current + emoji);
      return;
    }

    const start = box.selectionStart;
    const end = box.selectionEnd;
    const next = text.slice(0, start) + emoji + text.slice(end);

    setText(next);
    setPickerDismissed(false);
    noteTyping();

    // After React has written the new value, or the caret jumps to the end.
    requestAnimationFrame(() => {
      box.focus();
      box.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  }

  async function submit(): Promise<void> {
    // An unfinished command is not a message. Sending "/product bpc" verbatim would
    // put the agent's own tooling into the customer's transcript — so the command
    // comes out and whatever the agent wrote around it goes, which before this was
    // the whole box and is now only the command.
    const value = command === null ? text.trim() : withoutCommand(text, command).trim();

    if (
      (value.length === 0 && products.length === 0 && attachments.length === 0) ||
      sending ||
      uploading
    ) {
      return;
    }

    setSending(true);

    try {
      await onSend(
        value,
        products.map((product) => product.productId),
        attachments.map((attachment) => attachment.id),
      );
      setText("");
      setProducts([]);
      setAttachments([]);
      setAttachError(null);
      setPickerDismissed(false);

      if (typingRef.current) {
        typingRef.current = false;
        onTyping(false);
      }
    } finally {
      setSending(false);
    }
  }

  /**
   * Upload the chosen files, then hold their ids until the message is sent.
   *
   * Uploaded immediately rather than on send, so the agent finds out now that a file
   * is too large — not after writing a reply around it. The ids are what the message
   * carries; the bytes are already on the server by then.
   */
  async function attach(files: FileList | null): Promise<void> {
    if (files === null || files.length === 0) return;

    setUploading(true);
    setAttachError(null);

    try {
      for (const file of Array.from(files)) {
        const intent = await onUpload(file);

        if (intent === null) {
          setAttachError(`${file.name} could not be attached.`);
          continue;
        }

        setAttachments((current) => [...current, { id: intent.attachmentId, name: file.name }]);
      }
    } finally {
      setUploading(false);
      // Cleared so choosing the same file twice in a row still fires a change event.
      if (fileRef.current !== null) fileRef.current.value = "";
    }
  }

  function pick(product: CatalogProductDto): void {
    setProducts((current) =>
      // Picking the same product twice is a slip, not a request for two cards.
      current.some((entry) => entry.productId === product.productId)
        ? current
        : [...current, product],
    );

    /**
     * The command has done its job and is cut out; the sentence it sat in stays.
     *
     * Recomputed from the current text rather than closed over, because the agent
     * can keep typing while the results are on screen and the span moves as they do.
     */
    const typed = productCommand(text);
    const remaining = typed === null ? "" : withoutCommand(text, typed);

    setText(remaining);
    setPickerDismissed(false);

    /**
     * Focus goes back to the box, with the caret where the command used to be.
     *
     * Picking a product is a step in the middle of writing a reply, and an agent who
     * has to click back into the box to carry on has been interrupted by the tool
     * that was supposed to save them the trip. After the render, or React writes the
     * value afterwards and puts the caret at the end.
     */
    const caret = typed === null ? remaining.length : typed.start;

    requestAnimationFrame(() => {
      const box = boxRef.current;

      if (box === null) return;

      box.focus();
      box.setSelectionRange(caret, caret);
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === "Escape" && pickerOpen) {
      event.preventDefault();
      setPickerDismissed(true);
      return;
    }

    // Enter sends, Shift+Enter breaks the line — the convention every agent already
    // has in their fingers from every other chat tool.
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      // While the picker is open, Enter would send an empty message and lose the
      // search the agent is halfway through.
      if (!pickerOpen) void submit();
    }
  }

  if (requiresJoin) {
    return (
      <div className="composer">
        <p className="muted small" style={{ margin: 0 }}>
          Join the conversation to reply. The customer will be told you have arrived.
        </p>
      </div>
    );
  }

  /**
   * An attachment on its own is a message: a photo of a replacement part needs no
   * caption. Blocked while an upload is in flight, so send cannot outrun the bytes.
   *
   * What survives removing the command is the test, not whether one is present: an
   * agent who wrote a sentence and then ran the command still has a message to send,
   * and disabling Send on them would look like the dashboard had frozen.
   */
  const remaining = command === null ? text : withoutCommand(text, command);
  const canSend =
    !uploading && (remaining.trim().length > 0 || products.length > 0 || attachments.length > 0);

  return (
    <div className="composer-wrap">
      {searching !== null && (
        <ProductPicker
          query={searching.query}
          onPick={pick}
          onDismiss={() => {
            setPickerDismissed(true);
          }}
        />
      )}

      {products.length > 0 && (
        <div className="product-chips">
          {products.map((product) => (
            <span className="chip" key={product.productId}>
              {product.name}
              <button
                type="button"
                aria-label={`Remove ${product.name}`}
                onClick={() => {
                  setProducts((current) =>
                    current.filter((entry) => entry.productId !== product.productId),
                  );
                }}
              >
                ✕
              </button>
            </span>
          ))}
          {products.length >= MAX_PRODUCTS && (
            <span className="small muted">Maximum {MAX_PRODUCTS} products.</span>
          )}
        </div>
      )}

      {emojiOpen && (
        <div className="emoji-picker">
          {EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              aria-label={`Insert ${emoji}`}
              // Prevented so the click never moves focus out of the reply box —
              // the caret stays where the agent left it and the emoji lands there
              // rather than at the end.
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                insertEmoji(emoji);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {(attachments.length > 0 || attachError !== null) && (
        <div className="product-chips">
          {attachments.map((attachment) => (
            <span className="chip" key={attachment.id}>
              {attachment.name}
              <button
                type="button"
                aria-label={`Remove ${attachment.name}`}
                onClick={() => {
                  setAttachments((current) =>
                    current.filter((entry) => entry.id !== attachment.id),
                  );
                }}
              >
                ×
              </button>
            </span>
          ))}
          {attachError !== null && <span className="small muted">{attachError}</span>}
        </div>
      )}

      <div className="composer">
        <button
          type="button"
          className="emoji-toggle"
          aria-label="Add an emoji"
          aria-expanded={emojiOpen}
          disabled={disabled || sending}
          onClick={() => {
            setEmojiOpen((open) => !open);
          }}
        >
          🙂
        </button>

        <button
          type="button"
          className="emoji-toggle"
          aria-label="Attach a file"
          disabled={disabled || sending || uploading}
          onClick={() => {
            fileRef.current?.click();
          }}
        >
          {uploading ? "…" : "📎"}
        </button>

        {/*
          Hidden, and driven by the button above. A bare file input cannot be styled
          to match anything, and every browser renders it differently.
        */}
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={ACCEPTED_FILES}
          style={{ display: "none" }}
          onChange={(event) => {
            void attach(event.target.files);
          }}
        />

        {/*
          Take a photo, rather than find one.

          `capture` is what turns a file picker into the camera, and it is a
          separate input because the same element cannot be both: adding `capture`
          to the one above would stop an agent at a desk from choosing a file at
          all. The button is hidden on a mouse-and-keyboard machine, where it would
          open the same picker twice over.
        */}
        <button
          type="button"
          className="emoji-toggle touch-only"
          aria-label="Take a photo"
          disabled={disabled || sending || uploading}
          onClick={() => {
            cameraRef.current?.click();
          }}
        >
          📷
        </button>

        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={(event) => {
            void attach(event.target.files);
          }}
        />

        <textarea
          ref={boxRef}
          value={text}
          placeholder={
            products.length > 0 ? "Add a note, or send the products…" : "Write a reply… (/pro)"
          }
          aria-label="Reply"
          disabled={disabled || sending}
          onChange={(event) => {
            setText(event.target.value);
            // Re-opens the picker when the agent edits the command after dismissing
            // it: dismissal referred to that search, not to the feature.
            setPickerDismissed(false);
            noteTyping();
          }}
          onKeyDown={onKeyDown}
        />
        <button
          className="primary"
          disabled={disabled || sending || !canSend}
          onClick={() => {
            void submit();
          }}
        >
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}
